import EventRepeat from "@mui/icons-material/EventRepeat";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DateCalendar, DateField } from "@mui/x-date-pickers";
import { FormSelect } from "components/Forms/FormSelect";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { Frequency } from "generated-graphql/graphql";
import { DateTime } from "luxon";
import pluralize from "pluralize";
import { useCallback, useRef, useState } from "react";
import { Control, Controller, useController, useWatch } from "react-hook-form";
import { TASK_FREQUENCY_ORDER, TaskFormType } from "./types";
import { getFrequencyLabel } from "./util.labels";

export const DueDatePopover = ({
  open,
  onClose,
  anchorEl,
  control,
}: {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  control: Control<TaskFormType>;
}) => {
  const frequency = useWatch({ control, name: "frequency" });
  const interval = useWatch({ control, name: "interval" });

  const { field: frequencyField } = useController({
    control,
    name: "frequency",
  });
  const { field: intervalField } = useController({
    control,
    name: "interval",
  });

  const [recurrenceSelectionOpen, setRecurrenceSelectionOpen] = useState(false);
  const [isCustomRecurrence, setIsCustomRecurrence] = useState(false);
  const popoverActionRef = useRef<{ updatePosition: () => void } | null>(null);

  const toggleRecurrenceSelection = () => {
    if (isCustomRecurrence) {
      setIsCustomRecurrence(false);
      return;
    }

    setRecurrenceSelectionOpen(!recurrenceSelectionOpen);
  };
  const closeRecurrenceSelection = () => setRecurrenceSelectionOpen(false);

  const handleSelectQuickFrequency = (value: Frequency) => {
    setIsCustomRecurrence(false);
    frequencyField.onChange(value);

    if (value !== Frequency.Once) {
      intervalField.onChange(1);
    }

    closeRecurrenceSelection();
  };

  const handleSelectCustom = () => {
    setIsCustomRecurrence(true);
    if (frequency === Frequency.Once) {
      // Default to Weekly when entering custom mode from "does not repeat"
      frequencyField.onChange(Frequency.Weekly);
    }
    if (!interval || interval < 1) {
      intervalField.onChange(1);
    }
    closeRecurrenceSelection();
  };

  const updateAnchorPosition = useCallback(
    () => popoverActionRef.current?.updatePosition(),
    []
  );

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "center", horizontal: "left" }}
      action={popoverActionRef}
    >
      <Stack sx={{ width: "320px" }}>
        <Controller
          name="deadline"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            const handleChange = (newValue: DateTime | null) => {
              if (newValue instanceof Date) {
                onChange(newValue);
              } else {
                onChange(newValue?.toJSDate());
              }
            };

            return (
              <>
                <DateField
                  sx={{ px: 2, py: 2, pb: 0 }}
                  size="small"
                  name="deadline"
                  required={true}
                  fullWidth
                  onChange={handleChange}
                  value={normalizeDateValue(value)}
                  helperText={error?.message}
                  slotProps={{
                    textField: {
                      error: !!error,
                    },
                  }}
                />
                <DateCalendar
                  value={normalizeDateValue(value)}
                  onChange={handleChange}
                />
              </>
            );
          }}
        />
        <Divider />
        <List disablePadding>
          <ListItemButton onClick={toggleRecurrenceSelection}>
            <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>
              <EventRepeat />
            </ListItemIcon>
            <ListItemText
              primary={getRecurrenceLabel({
                isCustomRecurrence,
                frequency,
                interval,
              })}
            />
            {recurrenceSelectionOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>

          <Collapse in={isCustomRecurrence}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ p: 2 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flex: 1.5, maxWidth: "85px" }}
              >
                Repeat every
              </Typography>
              <TextField
                required
                name="interval"
                value={interval}
                onChange={(e) => intervalField.onChange(Number(e.target.value))}
                hiddenLabel
                size="small"
                type="number"
                inputMode="numeric"
                variant="filled"
                slotProps={{ input: { inputProps: { min: 1 } } }}
                sx={{ flex: 1 }}
              />
              <FormSelect
                name="frequency"
                variant="filled"
                label=""
                hideErrors
                sx={{ flex: 2 }}
                selectProps={{
                  size: "small",
                  hiddenLabel: true,
                }}
                control={control}
                clearable={false}
                selectItems={Object.values(Frequency)
                  .filter((f) => f !== Frequency.Once)
                  .sort(
                    (a, b) =>
                      TASK_FREQUENCY_ORDER.indexOf(a) -
                      TASK_FREQUENCY_ORDER.indexOf(b)
                  )
                  .map((value) => ({
                    value,
                    display: getIntervalLabel(value, interval),
                  }))}
              />
            </Stack>
          </Collapse>

          <Collapse
            in={recurrenceSelectionOpen}
            onEntered={updateAnchorPosition}
            onExited={updateAnchorPosition}
          >
            {Object.values(Frequency)
              .sort(
                (a, b) =>
                  TASK_FREQUENCY_ORDER.indexOf(a) -
                  TASK_FREQUENCY_ORDER.indexOf(b)
              )
              .map((value) => (
                <ListItemButton
                  selected={frequency === value && interval === 1}
                  key={value}
                  onClick={() => handleSelectQuickFrequency(value)}
                >
                  <ListItemText
                    primary={
                      value === Frequency.Once
                        ? "Does not repeat"
                        : prettyPrintEnumValue(value)
                    }
                  />
                </ListItemButton>
              ))}
            <Divider />
            <ListItemButton
              selected={interval !== 1 && frequency !== Frequency.Once}
              onClick={handleSelectCustom}
            >
              <ListItemText primary="Custom..." />
            </ListItemButton>
          </Collapse>
        </List>
      </Stack>
    </Popover>
  );
};

const getIntervalLabel = (frequency: Frequency, interval: number) => {
  switch (frequency) {
    case Frequency.Weekly:
    case Frequency.Monthly:
    case Frequency.Quarterly:
    case Frequency.Yearly:
      return pluralize(getFrequencyLabel(frequency), interval);

    case Frequency.Once:
      return "";
  }
};

const getRecurrenceLabel = ({
  isCustomRecurrence,
  frequency,
  interval,
}: {
  isCustomRecurrence: boolean;
  frequency: Frequency;
  interval: number;
}) => {
  if (isCustomRecurrence) {
    return "Custom recurrence";
  }

  if (frequency === Frequency.Once) {
    return "Does not repeat";
  }

  if (interval === 1) {
    return `Every ${getFrequencyLabel(frequency)}`;
  }

  return `Every ${interval} ${pluralize(
    getFrequencyLabel(frequency),
    interval
  )}`;
};

const normalizeDateValue = (
  value: string | Date | DateTime | null
): DateTime | null => {
  if (value instanceof Date) {
    return DateTime.fromJSDate(value);
  }
  if (value instanceof DateTime) {
    return value;
  }
  if (typeof value === "string") {
    return DateTime.fromISO(value);
  }
  return null;
};
